/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage, { LazyImageProps } from "@stoneage/MobiusOverrides/LazyImage";
import { xScreen } from "@stoneage/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

const enum fields {
    firstImagePath = "firstImagePath",
    secondImagePath = "secondImagePath",
    firstImageAltText = "firstImageAltText",
    secondImageAltText = "secondImageAltText",
    imageWidth = "imageWidth",
    imageHeight = "imageHeight",
    smallImageWidth = "smallImageWidth",
    smallImageHeight = "smallImageHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.firstImagePath]: string;
        [fields.secondImagePath]: string;
        [fields.firstImageAltText]: string;
        [fields.secondImageAltText]: string;
        [fields.imageWidth]: string;
        [fields.imageHeight]: string;
        [fields.smallImageWidth]: string;
        [fields.smallImageHeight]: string;
    };
}

type Props = OwnProps;

export interface DoubleImageStyles {
    widgetContainer?: InjectableCss;
    imagesWrapper?: InjectableCss;
    firstImageWrapper?: InjectableCss;
    secondImageWrapper?: InjectableCss;
}

export const doubleImageStyles: DoubleImageStyles = {
    widgetContainer: {
        css: css`
            ${xScreen("gte", "lg")} {
                padding-left: 30px;
            }
            ${xScreen("gte", "xl")} {
                padding-left: 80px;
            }
        `,
    },
    imagesWrapper: {
        css: css`
            position: relative;
        `,
    },
};

const DoubleImage = ({ fields }: Props) => {
    const styles = doubleImageStyles;

    const {
        firstImagePath,
        secondImagePath,
        firstImageAltText,
        secondImageAltText,
        imageWidth,
        imageHeight,
        smallImageWidth,
        smallImageHeight,
    } = fields;

    return (
        <StyledWrapper {...styles.widgetContainer}>
            <StyledWrapper {...styles.imagesWrapper}>
                <StyledWrapper
                    {...styles.firstImageWrapper}
                    css={css`
                        width: 60%;
                        max-width: ${imageWidth.replace("px", "")}px;
                        max-height: ${imageHeight.replace("px", "")}px;
                        position: relative;
                        top: 0;
                        left: 0;
                        z-index: 2;

                        ${xScreen("lte", "md")} {
                            max-width: ${smallImageWidth || imageWidth};
                            max-height: ${smallImageHeight || imageHeight};
                        }
                    `}
                >
                    <LazyImage
                        src={firstImagePath}
                        altText={firstImageAltText}
                        isCatalogImage={false}
                        height={imageHeight}
                        width={imageWidth}
                    />
                </StyledWrapper>
                <StyledWrapper
                    {...styles.secondImageWrapper}
                    css={css`
                        width: 60%;
                        max-width: ${imageWidth.replace("px", "")}px;
                        max-height: ${imageHeight.replace("px", "")}px;
                        position: absolute;
                        bottom: -60px;
                        left: 194px;
                        z-index: 1;

                        ${xScreen("lte", "md")} {
                            bottom: -40px;
                            left: 114px;
                            max-width: ${smallImageWidth || imageWidth};
                            max-height: ${smallImageHeight || imageHeight};
                        }

                        ${xScreen("lte", "xs")} {
                            left: auto;
                            right: 0;
                        }
                    `}
                >
                    <LazyImage
                        src={secondImagePath}
                        altText={secondImageAltText}
                        isCatalogImage={false}
                        height={imageHeight}
                        width={imageWidth}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const doubleImage: WidgetModule = {
    component: DoubleImage,
    definition: {
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: fields.firstImagePath,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.firstImageAltText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.secondImagePath,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.secondImageAltText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.imageWidth,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("max-width", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.imageHeight,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("max-height", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.smallImageWidth,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                displayName: "width for Tablet/Mobile",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("max-width", value) ? null : `Invalid Input`;
                },
            },
            {
                name: fields.smallImageHeight,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                displayName: "height for Tablet/Mobile",
                validate: (value: string | null) => {
                    if (!value) {
                        return null;
                    }
                    return CSS.supports("max-height", value) ? null : `Invalid Input`;
                },
            },
        ],
    },
};

export default doubleImage;
